import { type ProcessUploadResponse, useMutation, useUploadEvidence } from 'api';
import { logger } from 'monitor';
import { useCallback } from 'react';

import { extractExifData } from '../../../evidence';
import type { Bale } from '../../types';
import { formatBaleReason } from '../../utils/formatBaleReason';
import { trackBaleSopOutcome } from '../../utils/trackBaleSopOutcome';
import { useAnalysisStatus } from '../useAnalysisStatus/useAnalysisStatus';
import { useBaleAnalysisStore } from '../useBaleAnalysis/useBaleAnalysisStore';

export const useUploadBale = () => {
  const {
    mode,
    parentId,
    actions: { addBale, updateBale },
  } = useBaleAnalysisStore();
  const { mutateAsync: uploadEvidence } = useUploadEvidence(mode);
  const { getStatusAndRejectionReason } = useAnalysisStatus();

  const uploadBale = useCallback(
    async ({ bale, model, startTime }: { bale: Bale; model?: string; startTime: number }) => {
      const exifData = await extractExifData(bale.photo);
      return uploadEvidence({
        metadata: { taken_at: exifData.timestamp },
        model,
        onProgress: (progress) => {
          updateBale({ ...bale, uploadProgress: progress });
        },
        parentId,
        photo: bale.photo,
        startTime,
      });
    },
    [uploadEvidence, updateBale, parentId]
  );

  return useMutation<ProcessUploadResponse, Error, { bale: Bale; model?: string; startTime: number }>({
    mutationFn: uploadBale,
    onError: (error, { bale }) => {
      const analysisStatus =
        error?.message === 'invalid file format' ? ('rejected-photo' as const) : ('retry' as const);

      const isPhotoRejected = analysisStatus === 'rejected-photo';
      const formattedReason = isPhotoRejected ? formatBaleReason(error?.message) : undefined;

      const failedBale = {
        ...bale,
        analysisStatus,
        photoRejectedReason: formattedReason,
      };

      if (isPhotoRejected) {
        trackBaleSopOutcome(failedBale, 'model');
      } else {
        logger.error(error.message, error);
      }

      updateBale({
        ...failedBale,
        isLoading: false,
      });
    },
    onMutate: ({ bale }) => {
      addBale({ ...bale, isLoading: true });
    },
    onSuccess: (analysis, { bale }) => {
      logger.debug('Bale analysis', {
        analysis,
        bale,
      });
      const analysisResult = getStatusAndRejectionReason(mode, analysis.model_results);

      const analyzedBale = {
        ...bale,
        ...analysisResult,
        analysis,
        evidenceId: analysis.evidence_id,
        isLoading: false,
      };

      trackBaleSopOutcome(analyzedBale, 'model');
      updateBale(analyzedBale);

      return analysis;
    },
  });
};
